import * as React from "react";

import { normalizeRealtimeTags, type RealtimeTag } from "@blis/realtime/tags";

import { leadingTrailingThrottle } from "../throttle";
import { getRealtimeClient, type RealtimeStatus } from "./client";

export type InvalidateTarget = () => Promise<unknown> | unknown;

export type UseRealtimeInvalidateOptions = {
  tags: readonly RealtimeTag[];
  targets: readonly InvalidateTarget[];
  /**
   * If `true` (default), fire one invalidate immediately on
   * (re)connect to catch up on anything that happened while the client
   * was disconnected.
   */
  catchUpOnReconnect?: boolean;
  /**
   * If `false`, the hook is a no-op.
   */
  enabled?: boolean;
  /**
   * Coalesce bursts of events into at most one trailing invalidate per
   * `throttleMs` window. Leading-edge: the first event in a quiet
   * window fires immediately so the UI feels responsive; subsequent
   * events within the window collapse into a single trailing refetch.
   *
   * Defaults to 200ms — short enough to feel real-time, long enough to
   * absorb the storm from a 100-row batch worker run that fires 100
   * NOTIFYs in <1s. Set to 0 to disable.
   */
  throttleMs?: number;
};

/**
 * Subscribe to one or more realtime invalidation tags and run the supplied
 * targets whenever any tag arrives.
 */
export function useRealtimeInvalidate({
  tags,
  targets,
  throttleMs = 200,
  catchUpOnReconnect = true,
  enabled = true,
}: UseRealtimeInvalidateOptions): void {
  const client = getRealtimeClient();
  const targetsRef = React.useRef(targets);
  targetsRef.current = targets;

  const tagsKey = React.useMemo(
    () => normalizeRealtimeTags(tags).join("|"),
    [tags],
  );

  const runInvalidates = React.useCallback(() => {
    for (const target of targetsRef.current) {
      try {
        void target();
      } catch {
        // Don't let one bad target stop the rest.
      }
    }
  }, []);

  React.useEffect(() => {
    if (!enabled || !tagsKey) return;

    const normalizedTags = tagsKey.split("|") as RealtimeTag[];

    const throttled = leadingTrailingThrottle(runInvalidates, throttleMs);
    const unsubscribeInvalidate = client.subscribe(normalizedTags, () => {
      throttled.call();
    });
    const unsubscribeReconnect = catchUpOnReconnect
      ? client.subscribeReconnect(runInvalidates)
      : null;

    return () => {
      throttled.cancel();
      unsubscribeInvalidate();
      unsubscribeReconnect?.();
    };
  }, [
    catchUpOnReconnect,
    client,
    enabled,
    runInvalidates,
    tagsKey,
    throttleMs,
  ]);
}

/**
 * React hook returning the current realtime connection status. Useful
 * for surfaces that want to show a "live" indicator (or, more usefully,
 * a "reconnecting" / "offline" hint) so users don't think the page is
 * silently stale.
 *
 * Implemented via {@link React.useSyncExternalStore} so it's safe under
 * concurrent rendering and avoids redundant renders when status hasn't
 * changed.
 */
export function useRealtimeStatus(): RealtimeStatus {
  const client = getRealtimeClient();
  return React.useSyncExternalStore(
    React.useCallback((notify) => client.subscribeStatus(notify), [client]),
    () => client.getStatus(),
    () => "closed",
  );
}
